import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PlaceholdersService } from 'src/app/services/placeholders/placeholders.service';


@Pipe({
  name: 'placeholderHighlight',
  pure: false
})
export class PlaceholderHighlightPipe implements PipeTransform {

  // {Token} -> captures "Token"
  private readonly tokenRegex = /\{([a-zA-Z0-9_]+)\}/g;

  // Last rendered HTML and its SafeHtml wrapper, reused while the output is unchanged
  private lastHtml?: string;
  private lastSafeHtml?: SafeHtml;

  constructor(
    private sanitizer: DomSanitizer,
    private placeholdersService: PlaceholdersService) {

  }

  /**
   * Resolves `{Token}` placeholders into highlighted values and makes step-text links open in a new tab.
   * Returns the same SafeHtml instance while the rendered HTML is unchanged, so change detection does not rewrite the host's innerHTML.
   * @param value Step HTML that may contain placeholders and links.
   * @param editor When true, placeholders stay unresolved and are highlighted as tokens.
   */
  transform(value: string | null | undefined, editor: boolean = false): SafeHtml {
    // 6962: The pipe is impure and the app listens to every window mousedown, so returning a new SafeHtml on each
    // change detection re-rendered the step text between mousedown and mouseup, replacing the anchor and dropping the click.
    const html = value ? this.openLinksInNewTab(this.highlight(value, editor)) : '';
    if (html !== this.lastHtml || !this.lastSafeHtml) {
      this.lastHtml = html;
      this.lastSafeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this.lastSafeHtml;
  }

  /**
   * Replaces `{Token}` placeholders with their resolved value, or highlights the raw token when unresolved or in the editor.
   * @param value Step HTML containing placeholders.
   * @param editor When true, tokens are never resolved.
   */
  private highlight(value: string, editor: boolean): string {
    return value.replace(this.tokenRegex, (match: string, _token: string) => {
      // If your service expects "{Token}" keep 'match'; if it expects "Token", pass _token.
      const raw = this.placeholdersService.getValue(match); // or getValue(_token)
      if (raw !== undefined && raw !== null && raw !== '' && !editor) {
        return `<b style="border-bottom:1px dotted #ddd; color:#285E87">${this.escapeHtml(String(raw))}</b>`;
      } else {
        return `<b style="color:#E04E39">${this.escapeHtml(match)}</b>`;
      }
    });
  }

  /**
   * Makes external links (procedure files, web addresses) open in a new tab; `#/redirect/` and other internal links are left as-is.
   * @param html Rendered step HTML.
   * @returns The HTML with external anchors targeting a new tab.
   */
  private openLinksInNewTab(html: string): string {
    if (!/<a\s/i.test(html)) {
      return html;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('a[href]').forEach(a => {
      const href = (a.getAttribute('href') ?? '').trim();
      if (/^www\./i.test(href)) {
        // Bare www addresses would otherwise resolve relative to ASCC
        a.setAttribute('href', `https://${href}`);
      } else if (!/^https?:\/\//i.test(href) || href.includes('/redirect/')) {
        return;
      }
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return template.innerHTML;
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, ch =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as const)[ch]!
    );
  }
}
